import React, { useCallback, useState } from 'react';
import { useFormField, useFormActions } from '../../core';
import clsx from 'clsx';
import { InputTextType } from '../../typings';
import { Button, Icon, Input, useClickAway } from '@/ui-kit';
import { usePreFilledFieldValidation } from '../../hooks';
import './InputText.scss';

type InputTextProps = {
  label: string;
  name: string;
  type?: InputTextType;
  placeholder?: string;
  showClearButton?: boolean;
  /** Amounts offered in a dropdown beside the input; any other value stays typable. */
  suggestions?: number[];
  /** Formik key of the group restriction a picked suggestion must also touch. */
  suggestionsErrorKey?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
};

/** Matches what the @currency mask renders for the same number. */
const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

type SuggestionsMenuProps = {
  label: string;
  fieldName: string;
  suggestions: number[];
  suggestionsErrorKey?: string;
  disabled: boolean;
  clearButton?: React.ReactNode;
};

/** Isolates useClickAway's document listeners to fields that actually offer suggestions. */
const SuggestionsMenu: React.FC<SuggestionsMenuProps> = ({
  label,
  fieldName,
  suggestions,
  suggestionsErrorKey,
  disabled,
  clearButton,
}) => {
  const { setFieldValue, setFieldTouched } = useFormActions();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapperRef = useClickAway(() => setMenuOpen(false));

  const onSuggestionClick = useCallback(
    async (amount: number) => {
      setMenuOpen(false);

      // The @currency mask stores bare digits, so a picked amount and a typed one
      // land on byte-identical values.
      await setFieldValue(fieldName, String(amount));

      await setFieldTouched(fieldName, true);

      // The floor message renders from the group restriction's synthetic key, not from
      // this field's own — touching only the field would leave a picked below-floor
      // amount silently unmarked until submit.
      if (suggestionsErrorKey) await setFieldTouched(suggestionsErrorKey, true);
    },
    [fieldName, suggestionsErrorKey, setFieldValue, setFieldTouched],
  );

  return (
    <div ref={menuWrapperRef}>
      {/* One positioned rail, so a field showing both controls lays them out side by side. */}
      <div className="input-text__adornments">
        {clearButton}
        <button
          type="button"
          className="input-text__suggestions-toggle"
          aria-label={`Show suggested amounts for ${label}`}
          aria-expanded={menuOpen}
          disabled={disabled}
          onClick={() => setMenuOpen(open => !open)}
        >
          <Icon type={menuOpen ? 'arrow-up' : 'arrow-down'} />
        </button>
      </div>
      {menuOpen && (
        <div className="input-text__suggestions" role="menu">
          {suggestions.map(amount => (
            <button
              key={amount}
              type="button"
              role="menuitem"
              className="input-text__suggestion"
              onClick={() => onSuggestionClick(amount)}
            >
              {currency.format(amount)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const InputText: React.FC<InputTextProps> = ({
  label,
  showClearButton,
  suggestions,
  suggestionsErrorKey,
  ...props
}) => {
  const [field, meta] = useFormField<string>(props);

  const error = meta.touched ? meta.error : undefined;

  usePreFilledFieldValidation(field, meta.touched);

  const { setFieldValue } = useFormActions();

  const hasSuggestions = Boolean(suggestions?.length) && props.type !== 'hidden';

  const onClearButtonClick = useCallback(async () => {
    await setFieldValue(field.name, '');
  }, [field.name, setFieldValue]);

  const containerClasses = clsx('input-text__container', {
    'input-text__container--hidden': props.type === 'hidden',
  });

  // Destructure value from field to avoid spreading unknown type
  const { value, ...fieldProps } = field;

  const clearButton = showClearButton && (
    <Button
      className="input-text__clear-button"
      onClick={onClearButtonClick}
      asRemoveIcon="danger"
    />
  );

  return (
    <div className={containerClasses}>
      <Input
        label={label}
        id={field.name}
        {...fieldProps}
        {...props}
        value={value}
        state={error ? 'error' : 'default'}
        stateText={error}
        className="with-trailing-icon"
      />
      {hasSuggestions ? (
        <SuggestionsMenu
          label={label}
          fieldName={field.name}
          suggestions={suggestions ?? []}
          suggestionsErrorKey={suggestionsErrorKey}
          disabled={props.disabled === true || props.readonly === true}
          clearButton={clearButton}
        />
      ) : (
        props.type !== 'hidden' &&
        showClearButton && (
          <div className="input-text__adornments">{clearButton}</div>
        )
      )}
    </div>
  );
};
